import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Directive, HostListener, OnInit } from '@angular/core';
import { ErrorHandlerService, RouterService } from '../../../services';
import {
  IContract,
  IHQTicketDetailsResponse,
  IMetadataTicketDetailsResponse,
  ITicketConversationEdit,
  ITicketMessage,
  ITicketMessageGroup,
  TicketingVersion,
  TicketIssueType,
} from '../../../models';
import { combineLatest, filter, interval, map, Observable, Subscription, take, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  AddMessageToTicket,
  getMetadataTicketDetails,
  getNewMessageActionState,
  getResidentAppSettings,
  getSelectedContract,
  getTicketChat,
  getTicketChatActionState,
  getTicketDetails,
  getTicketSettings,
  GetUnread,
  LoadTicketChat,
  UpdateTicketChat,
} from '../../../+state';
import { ActivatedRoute } from '@angular/router';
import { IActionState } from '../../../utils';

@UntilDestroy()
@Directive()
export abstract class TicketChatPageV2BaseComponent implements OnInit {
  public ticketDetails: IHQTicketDetailsResponse | IMetadataTicketDetailsResponse;
  public ticketChatArray: ITicketMessageGroup[] = [];
  public ticketChat$: Observable<ITicketMessageGroup[]> = this.store.select(getTicketChat).pipe(
    map((groups: ITicketMessageGroup[] = []) => [...groups].reverse()),
    tap(groups => (this.ticketChatArray = groups))
  );
  public ticketingVersion$: Observable<TicketingVersion> = this.store
    .select(getResidentAppSettings)
    .pipe(
      map(response => response?.ticketingVersion),
      filter(response => response !== null)
    );
  public selectedContract$: Observable<IContract> = this.store.select(getSelectedContract);
  public ticketChatActionState$: Observable<IActionState> =
    this.store.select(getTicketChatActionState);
  public newMessageActionState$: Observable<IActionState> =
    this.store.select(getNewMessageActionState);
  public isReadOnly = false;
  public isInitialLoad = true;
  public ticketId: string;
  public subscriptions: Subscription[] = [];
  private updateInterval = 30000;

  constructor(
    protected readonly routerService: RouterService,
    protected store: Store,
    protected errorHandlerService: ErrorHandlerService,
    private route: ActivatedRoute
  ) {}

  @HostListener('unloaded')
  pageDestroy() {
    this.subscriptions.forEach(s => s?.unsubscribe());
  }

  ngOnInit(): void {
    const sub = this.route.params.pipe(take(1)).subscribe(params => {
      this.ticketId = params['id'];
      if (this.ticketId) {
        this.getTicketDetails();
        this.loadTicketChat();
        this.refreshUnreadOnRead();
        this.updateChat();
        this.setupAutoScroll();
        this.resolveReadOnly();
        this.isInitialLoad = false;
      }
    });
    this.subscriptions.push(sub);
  }

  public goBack(): void {
    void this.routerService.navigate(['content', 'concern']);
  }

  public sendMessage(newMessage: string | ITicketMessage): void {
    // TODO: Has to be updated for retry
    const message: ITicketConversationEdit = {
      ticketId: this.ticketId,
      message: newMessage as string,
      attachments: [],
    };
    this.store.dispatch(AddMessageToTicket({ message }));
  }

  protected loadTicketChat(): void {
    this.store.dispatch(LoadTicketChat({ ticketId: this.ticketId }));
  }

  // ART-19541: opening the messenger loads the chat (getTicketConversationV2), which
  // is the point the backend marks the conversation read. Once that load succeeds,
  // refresh the shared getUnread slice so the ticket-card dot, overview tab bubbles
  // and main-nav dot clear. This refresh used to live on the ticket-details page
  // (ART-19298 S4); it belongs here because the details view no longer fetches the
  // chat. Gate only on the chat load completing — NOT on ticket-details state — so it
  // also fires when the messenger is opened straight from a push notification
  // (navigateToTicketChat), where the details store is never loaded for this ticket.
  // loadTicketChat() runs first in ngOnInit, so the action state is already reset to
  // pending here and we wait for this load's success (a failed initial load is later
  // rescued by the polling UpdateTicketChatSuccess, which also sets done).
  private refreshUnreadOnRead(): void {
    const sub = this.ticketChatActionState$
      .pipe(
        filter(chatState => chatState?.done),
        take(1),
        untilDestroyed(this)
      )
      .subscribe(() => this.store.dispatch(GetUnread()));
    this.subscriptions.push(sub);
  }

  private getTicketDetails(): void {
    const sub = this.ticketingVersion$.pipe(untilDestroyed(this)).subscribe(version => {
      if (version === TicketingVersion.HIERARCHICAL) {
        this.getHQTicketDetails();
      } else if (version === TicketingVersion.METADATA) {
        this.getMetadataTicketDetails();
      }
    });
    this.subscriptions.push(sub);
  }

  private getHQTicketDetails(): void {
    const sub = this.store
      .select(getTicketDetails)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        if (response) {
          this.ticketDetails = response;
        }
      });
    this.subscriptions.push(sub);
  }

  private getMetadataTicketDetails(): void {
    const sub = this.store
      .select(getMetadataTicketDetails)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        if (response) {
          this.ticketDetails = response;
        }
      });
    this.subscriptions.push(sub);
  }

  private resolveReadOnly(): void {
    const sub = combineLatest([
      this.store.select(getTicketSettings),
      this.store.select(getTicketDetails),
      this.store.select(getMetadataTicketDetails),
    ])
      .pipe(
        map(([ticketSettings, hqDetails, metadataDetails]) => {
          const details = metadataDetails || hqDetails;
          if (!ticketSettings || !details) return false;
          const issueType = details.issueType;
          return issueType === TicketIssueType.CONCERN
            ? ticketSettings.concernMessagesReadOnly
            : ticketSettings.damageMessagesReadOnly;
        }),
        untilDestroyed(this)
      )
      .subscribe(readOnly => {
        this.isReadOnly = readOnly ?? false;
      });
    this.subscriptions.push(sub);
  }

  private updateChat(): void {
    const intervalSub = interval(this.updateInterval)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.ticketId) {
          this.store.dispatch(UpdateTicketChat({ ticketId: this.ticketId }));
        }
      });
    this.subscriptions.push(intervalSub);
  }

  public abstract setupAutoScroll(): void;
}
